import { useEffect, useRef } from 'react';
import { useUiStore } from '../state/uiStore';
import { useBlobStore } from '../state/blobStore';
import { usePerson, type PersonRecord } from '../lib/queries';
import {
  COMPOSITION_LABEL,
  COUNTRY_LABEL,
  ECONOMIC_ACTIVITY_LABEL,
  SEX_LABEL,
  TENURE_LABEL,
} from '../lib/labels';
import { ageBandLabel, incomeBandLabel } from '../lib/bandLookup';
import PersonaSlot from './PersonaSlot';

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="side-panel__row">
      <dt className="side-panel__row-label">{label}</dt>
      <dd className={`side-panel__row-value${mono ? ' side-panel__row-value--mono' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const slug = title.toLowerCase().replace(/\s+/g, '-');
  return (
    <section className="side-panel__section" aria-labelledby={`section-${slug}`}>
      <h3 id={`section-${slug}`}>{title}</h3>
      <dl className="side-panel__rows">{children}</dl>
    </section>
  );
}

function fallbackPersonName(id: string): string {
  return `Person ${id.slice(0, 8)}`;
}

function PersonBody({ person }: { person: PersonRecord }) {
  const blob = useBlobStore((s) => s.blob);
  const ageLabel = ageBandLabel(blob, person.age_band);
  const sexLabel = SEX_LABEL[person.sex] ?? person.sex;
  const econLabel =
    ECONOMIC_ACTIVITY_LABEL[person.economic_activity] ?? person.economic_activity;
  const composition = person.household?.composition;
  const tenure = person.household?.tenure;
  const countryLabel = person.region
    ? COUNTRY_LABEL[person.region.country] ?? person.region.country
    : null;
  const personaName =
    person.persona_profile?.display_name ?? fallbackPersonName(person.id);

  return (
    <>
      <Section title="Identity">
        <Row label="Person ID" value={person.id} mono />
      </Section>

      <Section title="Demographics">
        <Row label="Age band" value={ageLabel} />
        <Row label="Sex" value={sexLabel} />
        <Row label="Occupation" value={person.occupation_class} />
        <Row label="Economic activity" value={econLabel} />
        {person.income_band != null && (
          <Row label="Income band" value={incomeBandLabel(blob, person.income_band)} />
        )}
      </Section>

      <Section title="Household">
        {person.household ? (
          <>
            <Row
              label="Composition"
              value={composition ? COMPOSITION_LABEL[composition] ?? composition : '—'}
            />
            <Row label="Size" value={`${person.household.size}`} />
            <Row
              label="Tenure"
              value={tenure ? TENURE_LABEL[tenure] ?? tenure : '—'}
            />
            <Row
              label="Household income band"
              value={incomeBandLabel(blob, person.household.income_band)}
            />
          </>
        ) : (
          <p className="side-panel__placeholder">Household record not found.</p>
        )}
      </Section>

      <Section title="Location">
        <Row label="Region code" value={person.region_code} mono />
        {person.region && (
          <>
            <Row label="Region name" value={person.region.name} />
            {countryLabel && <Row label="Country" value={countryLabel} />}
          </>
        )}
      </Section>

      <PersonaSlot displayName={personaName} />
    </>
  );
}

function SkeletonRow() {
  return (
    <div className="side-panel__row">
      <span className="skeleton skeleton--label" aria-hidden="true" />
      <span className="skeleton skeleton--value" aria-hidden="true" />
    </div>
  );
}

function SkeletonSection({ title, rows }: { title: string; rows: number }) {
  return (
    <section className="side-panel__section" aria-hidden="true">
      <h3>{title}</h3>
      <div className="side-panel__rows">
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    </section>
  );
}

function PersonSkeleton() {
  return (
    <div aria-live="polite" aria-busy="true">
      <span className="visually-hidden">Loading person details…</span>
      <SkeletonSection title="Identity" rows={1} />
      <SkeletonSection title="Demographics" rows={4} />
      <SkeletonSection title="Household" rows={4} />
      <SkeletonSection title="Location" rows={3} />
    </div>
  );
}

export default function SidePanel() {
  const panelOpen = useUiStore((s) => s.panelOpen);
  const selectedPersonId = useUiStore((s) => s.selectedPersonId);
  const closePanel = useUiStore((s) => s.closePanel);
  const { data, isLoading, error, refetch } = usePerson(selectedPersonId);

  const asideRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    if (panelOpen) {
      el.removeAttribute('inert');
    } else {
      el.setAttribute('inert', '');
    }
  }, [panelOpen]);

  useEffect(() => {
    if (panelOpen && !wasOpenRef.current) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      closeButtonRef.current?.focus();
      wasOpenRef.current = true;
    } else if (!panelOpen && wasOpenRef.current) {
      const prev = previousFocusRef.current;
      if (prev && document.contains(prev)) {
        prev.focus();
      }
      wasOpenRef.current = false;
    }
  }, [panelOpen]);

  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panelOpen, closePanel]);

  const heading = selectedPersonId
    ? data?.persona_profile?.display_name ?? fallbackPersonName(selectedPersonId)
    : 'No selection';

  return (
    <aside
      ref={asideRef}
      className={`side-panel${panelOpen ? ' side-panel--open' : ''}`}
      aria-label="Selected person details"
    >
      <header className="side-panel__header">
        <h2>{heading}</h2>
        <button
          ref={closeButtonRef}
          type="button"
          className="side-panel__close"
          onClick={closePanel}
          aria-label="Close panel"
        >
          ×
        </button>
      </header>
      <div className="side-panel__body">
        {!selectedPersonId && (
          <p className="side-panel__placeholder">Click a dot on the map to see details.</p>
        )}
        {selectedPersonId && isLoading && <PersonSkeleton />}
        {selectedPersonId && error && (
          <div className="side-panel__error" role="alert">
            <strong>Could not load person.</strong>
            <span className="side-panel__error-detail">{error.message}</span>
            <button
              type="button"
              className="side-panel__error-retry"
              onClick={() => refetch()}
            >
              Try again
            </button>
          </div>
        )}
        {selectedPersonId && data && <PersonBody person={data} />}
      </div>
    </aside>
  );
}
